import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid, TextareaAutosize } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Link, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import dayjs from 'dayjs'
import PageLayout from '../common/PageLayout'
import PageHeading from '../common/PageHeading'
import Divider from '../../../../components/atoms/Divider'
import Loading from '../../../../components/molecules/Loading'
import FlexRow from '../../../../components/molecules/FlexRow'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import SydButton from '../../../../components/commonDesign/Button'
import SydLinkButton from '../../../../components/commonDesign/LinkButton'
import SydModal from '../../../../components/commonDesign/SydModal'
import SydModalActions from '../../../../components/commonDesign/SydModal/SydModalActions'
import SydLabel from '../../../../components/commonDesign/SydLabel'
import Icon from '../../../../components/atoms/Icon'
import FastAvatar from '../../../../components/molecules/FastAvatar'
import { useClient } from '../../../../api/clients'
import SydInput from '../../../../components/commonDesign/SydInput'
import SydSelect from '../../../../components/commonDesign/SydSelect'
import WYSIWYGEditor from '../../../../components/organisms/WYSIWYGEditor'
import { useGetMeeting, useGetMeetingTranscript, useCancelMeeting, useRegenerateSummary, useAddMeetingSummary, useUpdateMeetingNotes } from '../../../../api/meetings'
import MeetingStatusBadge from './MeetingStatusBadge'
import { TERMINAL_STATUSES, SUMMARY_TYPES } from './meetingConstants'

const useStyles = makeStyles(() => ({
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: '8px 16px',
    fontSize: 'var(--font-size-sm)'
  },
  infoLabel: {
    color: 'var(--dai-color-text-dim)',
    fontWeight: 500
  },
  link: {
    color: 'var(--color-primary-600)',
    textDecoration: 'none',
    wordBreak: 'break-all',
    '&:hover': { textDecoration: 'underline' }
  },
  summaryCard: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    overflow: 'hidden'
  },
  summaryHeader: {
    padding: '10px 14px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:hover': { backgroundColor: 'var(--dai-color-hover)' }
  },
  summaryBody: {
    padding: '0 14px 14px',
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1.7,
    '& p': { margin: '0 0 8px' },
    '& ul, & ol': { margin: '0 0 8px', paddingLeft: '20px' },
    '& li': { marginBottom: '4px' }
  },
  statusDot: {
    width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flexShrink: 0
  },
  eventsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--font-size-xs)',
    marginTop: 'var(--margin-sm)',
    '& th': {
      textAlign: 'left',
      padding: '6px 10px',
      fontWeight: 600,
      color: 'var(--dai-color-text-dim)',
      borderBottom: '1px solid var(--dai-color-border1)'
    },
    '& td': {
      padding: '5px 10px',
      borderBottom: '1px solid var(--dai-color-border1)',
      color: 'var(--dai-color-text-dim)'
    },
    '& tr:last-child td': {
      borderBottom: 'none'
    }
  },
  eventType: {
    fontFamily: 'monospace',
    fontSize: 'var(--font-size-xxs)'
  },
  breadcrumb: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--dai-color-text-dim)',
    textDecoration: 'none',
    '&:hover': { color: 'var(--color-primary-600)', textDecoration: 'underline' }
  },
  notesEditor: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    minHeight: 140
  },
  modalPromptArea: {
    width: '100%',
    fontFamily: 'inherit',
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1.6,
    padding: '10px 12px',
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    resize: 'vertical'
  },
  saveStatusHint: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--dai-color-text-dim)',
    marginLeft: 4
  },
  toggleBtn: {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: 'var(--border-radius-md)',
    border: 'var(--border-neutral-thin)',
    backgroundColor: 'transparent',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--dai-color-text)',
    transition: 'background-color 150ms ease',
    '&:hover': { backgroundColor: 'var(--dai-color-hover)' }
  }
}))

const PLATFORM_LABELS = {
  teams: 'Teams', zoom: 'Zoom', google_meet: 'Google Meet', webex: 'Webex', unknown: 'Meeting'
}

const AUTOSAVE_DELAY = 1500

const STATUS_COLORS = {
  pending: 'var(--color-warning-500)',
  processing: 'var(--color-info-500)',
  completed: 'var(--color-success-500)',
  failed: 'var(--color-danger-500)'
}

function MeetingDetail () {
  const classes = useStyles()
  const { meetingId } = useParams()
  const { data: meeting, isLoading } = useGetMeeting(meetingId)
  const { data: client } = useClient(meeting?.clientId, { enabled: !!meeting?.clientId })
  const isTerminal = meeting ? TERMINAL_STATUSES.includes(meeting.status) : false
  const isEditable = meeting ? ['pending', 'scheduled', 'completed', 'failed', 'cancelled'].includes(meeting.status) : false
  const { mutateAsync: cancelMeeting, isLoading: cancelling } = useCancelMeeting()
  const { mutateAsync: regenerateSummary, isLoading: regenerating } = useRegenerateSummary()
  const { mutateAsync: addMeetingSummary, isLoading: addingSummary } = useAddMeetingSummary()

  const { mutateAsync: updateMeetingNotes } = useUpdateMeetingNotes()
  const [saveStatus, setSaveStatus] = useState(null)
  const notesRef = useRef(null)
  const lastSavedRef = useRef(null)
  const debounceRef = useRef(null)
  const inflightRef = useRef(null)
  const dirty = useRef(false)
  const initialized = useRef(false)

  const doSave = useCallback(async () => {
    const value = notesRef.current
    if (value == null || !dirty.current) return
    setSaveStatus('saving')
    try {
      await updateMeetingNotes({ meetingId, userNotes: value })
      lastSavedRef.current = value
      if (notesRef.current === value) {
        dirty.current = false
      }
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
    }
  }, [updateMeetingNotes, meetingId])

  const flushNotesSave = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    if (inflightRef.current) {
      await inflightRef.current
    }
    if (dirty.current) {
      const p = doSave()
      inflightRef.current = p
      await p
      inflightRef.current = null
    }
  }, [doSave])

  const handleNotesChange = useCallback((md) => {
    notesRef.current = md
    if (!initialized.current) {
      initialized.current = true
      lastSavedRef.current = md
      return
    }
    if (md === lastSavedRef.current) return
    dirty.current = true
    setSaveStatus(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      const p = doSave()
      inflightRef.current = p
      p.finally(() => { inflightRef.current = null })
    }, AUTOSAVE_DELAY)
  }, [doSave])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const [enhancedPromptOpen, setEnhancedPromptOpen] = useState(false)
  const enhancedPreset = SUMMARY_TYPES.find(s => s.type === 'enhanced_notes')
  const [enhancedPromptValue, setEnhancedPromptValue] = useState('')

  const openEnhancedPromptModal = useCallback(async () => {
    await flushNotesSave()
    setEnhancedPromptValue(enhancedPreset?.prompt || '')
    setEnhancedPromptOpen(true)
  }, [flushNotesSave, enhancedPreset])

  const handleSubmitEnhancedNotes = useCallback(async () => {
    if (!enhancedPromptValue.trim()) return
    const existingSummary = meeting?.summaries?.find(s => s.type === 'enhanced_notes')
    try {
      if (existingSummary) {
        await regenerateSummary({ meetingSummaryId: existingSummary.meetingSummaryId, meetingId, prompt: enhancedPromptValue })
      } else {
        await addMeetingSummary({ meetingId, type: 'enhanced_notes', prompt: enhancedPromptValue })
      }
      setEnhancedPromptOpen(false)
    } catch (err) {
      window.alert(err.message || 'Failed to generate enhanced notes')
    }
  }, [enhancedPromptValue, meeting?.summaries, regenerateSummary, addMeetingSummary, meetingId])

  const [showEvents, setShowEvents] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const { data: transcriptData } = useGetMeetingTranscript(meetingId, { enabled: showTranscript })
  const [expandedSummaries, setExpandedSummaries] = useState({})
  const [showAddSummary, setShowAddSummary] = useState(false)
  const [newSummaryType, setNewSummaryType] = useState('')
  const [newSummaryCustomName, setNewSummaryCustomName] = useState('')
  const [newSummaryPrompt, setNewSummaryPrompt] = useState('')

  const toggleSummary = useCallback((id) => {
    setExpandedSummaries(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleCancel = useCallback(async () => {
    if (!window.confirm('Cancel this meeting?')) return
    try {
      await cancelMeeting({ meetingId })
    } catch (err) {
      window.alert(err.message || 'Failed to cancel meeting')
    }
  }, [cancelMeeting, meetingId])

  const handleRegenerate = useCallback(async (summary) => {
    try {
      await regenerateSummary({ meetingSummaryId: summary.meetingSummaryId, meetingId })
    } catch (err) {
      window.alert(err.message || 'Failed to regenerate summary')
    }
  }, [regenerateSummary, meetingId])

  const existingSummaryTypes = useMemo(
    () => new Set((meeting?.summaries || []).map(s => s.type)),
    [meeting?.summaries]
  )

  const addSummaryTypeOptions = useMemo(() => [
    ...SUMMARY_TYPES.filter(s => !s.hidden && !existingSummaryTypes.has(s.type)).map(s => ({ label: s.label, value: s.type })),
    { label: 'Custom', value: '__custom__' }
  ], [existingSummaryTypes])

  const handleAddSummaryTypeChange = useCallback((e) => {
    const value = e.target.value
    setNewSummaryType(value)
    if (value === '__custom__') {
      setNewSummaryCustomName('')
      setNewSummaryPrompt('')
    } else {
      setNewSummaryCustomName('')
      const preset = SUMMARY_TYPES.find(s => s.type === value)
      setNewSummaryPrompt(preset?.prompt || '')
    }
  }, [])

  const handleSubmitNewSummary = useCallback(async () => {
    const resolvedType = newSummaryType === '__custom__' ? newSummaryCustomName.trim() : newSummaryType
    if (!resolvedType || !newSummaryPrompt) return
    try {
      await addMeetingSummary({ meetingId, type: resolvedType, prompt: newSummaryPrompt })
      setShowAddSummary(false)
      setNewSummaryType('')
      setNewSummaryCustomName('')
      setNewSummaryPrompt('')
    } catch (err) {
      window.alert(err.message || 'Failed to add summary')
    }
  }, [addMeetingSummary, meetingId, newSummaryType, newSummaryCustomName, newSummaryPrompt])

  const events = useMemo(() => meeting?.events || [], [meeting?.events])

  if (isLoading || !meeting) {
    return <PageLayout variant='constrained'><Loading /></PageLayout>
  }

  const platform = PLATFORM_LABELS[meeting.platform] || PLATFORM_LABELS.unknown

  return (
    <PageLayout variant='constrained'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Link to='/denali-ai/meetings' className={classes.breadcrumb}>Meetings</Link>
          <PageHeading title={meeting.title || 'Untitled Meeting'}>
            <FlexRow className='gap-sm'>
              {!isTerminal && (
                <SydButton variant='ghost' size='sm' onClick={handleCancel} processing={cancelling}>
                  Cancel
                </SydButton>
              )}
              {isEditable && (
                <SydLinkButton variant='outline' size='sm' icon='edit' to={`/denali-ai/meetings/${meetingId}/edit`}>
                  Edit
                </SydLinkButton>
              )}
            </FlexRow>
          </PageHeading>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.infoGrid}>
            {client && (
              <>
                <span className={classes.infoLabel}>Client</span>
                <FlexRow className='gap-xs' style={{ gap: 6 }}>
                  <FastAvatar avatarUrl={client.avatarUrl} abbreviation={client.clientAbbreviation} size='xxs' />
                  <span>{client.shortName}</span>
                </FlexRow>
              </>
            )}
            <span className={classes.infoLabel}>Status</span>
            <span><MeetingStatusBadge status={meeting.status} /></span>
            <span className={classes.infoLabel}>Platform</span>
            <span>{platform}</span>
            <span className={classes.infoLabel}>Meeting URL</span>
            <a href={meeting.meetingUrl} target='_blank' rel='noopener noreferrer' className={classes.link}>
              {meeting.meetingUrl}
            </a>
            <span className={classes.infoLabel}>Notetaker</span>
            <span>{meeting.botName}</span>
            {meeting.joinAt && (
              <>
                <span className={classes.infoLabel}>Scheduled</span>
                <span>{dayjs(meeting.joinAt).format('MMM D, YYYY h:mm A')}</span>
              </>
            )}
            {meeting.durationSeconds > 0 && (
              <>
                <span className={classes.infoLabel}>Duration</span>
                <span>{Math.floor(meeting.durationSeconds / 60)}m {meeting.durationSeconds % 60}s</span>
              </>
            )}
            {meeting.meetingType && (
              <>
                <span className={classes.infoLabel}>Type</span>
                <span>{meeting.meetingType}</span>
              </>
            )}
          </div>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>
        <Grid item xs={12}>
          <FlexRow className='justify-between items-center' style={{ marginBottom: 8 }}>
            <FlexRow className='gap-sm items-center'>
              <span className='font-size-md font-weight-bold'>My Notes</span>
              {saveStatus === 'saving' && <span className={classes.saveStatusHint}>Saving...</span>}
              {saveStatus === 'saved' && <span className={classes.saveStatusHint} style={{ color: 'var(--color-success-500)' }}>Saved</span>}
              {saveStatus === 'error' && <span className={classes.saveStatusHint} style={{ color: 'var(--color-danger-500)' }}>Save failed</span>}
            </FlexRow>
            {meeting.status === 'completed' && (
              <SydButton
                variant='primary'
                size='sm'
                onClick={openEnhancedPromptModal}
                processing={addingSummary || regenerating}
              >
                {existingSummaryTypes.has('enhanced_notes') ? 'Regenerate Enhanced Notes' : 'Generate Enhanced Notes'}
              </SydButton>
            )}
          </FlexRow>
          <div className={classes.notesEditor}>
            <WYSIWYGEditor
              placeholder='Type your meeting notes here...'
              defaultValue={meeting?.userNotes || ''}
              onChange={handleNotesChange}
              isMarkdown
              enableAutofocus={false}
            />
          </div>
        </Grid>

        {(meeting.summaries?.length > 0 || meeting.status === 'completed') && (
          <>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <FlexRow className='justify-between items-center'>
                <span className='font-size-md font-weight-bold'>Summaries</span>
                {meeting.status === 'completed' && !showAddSummary && (
                  <SydButton variant='ghost' size='sm' onClick={() => setShowAddSummary(true)}>
                    + Add Summary
                  </SydButton>
                )}
              </FlexRow>
            </Grid>
            {meeting.summaries?.map(s => {
              const isOpen = expandedSummaries[s.meetingSummaryId] ?? s.status === 'completed'
              const dotColor = STATUS_COLORS[s.status] || 'var(--color-neutral-400)'

              return (
                <Grid item xs={12} key={s.meetingSummaryId}>
                  <div className={classes.summaryCard}>
                    <div className={classes.summaryHeader} onClick={() => toggleSummary(s.meetingSummaryId)}>
                      <FlexRow className='gap-sm'>
                        <span className={classes.statusDot} style={{ backgroundColor: dotColor }} />
                        <span className='font-size-sm font-weight-bold'>{s.type}</span>
                        {s.status !== 'completed' && (
                          <span className='font-size-xs' style={{ color: 'var(--dai-color-text-dim)' }}>
                            {s.status === 'pending' ? 'Pending' : s.status === 'processing' ? 'Processing...' : 'Failed'}
                          </span>
                        )}
                      </FlexRow>
                      <FlexRow className='gap-sm'>
                        {s.status === 'completed' && (
                          <SydButton variant='ghost' size='xs' onClick={(e) => { e.stopPropagation(); handleRegenerate(s) }}>
                            Regenerate
                          </SydButton>
                        )}
                        {s.status === 'failed' && (
                          <SydButton variant='ghost' size='xs' onClick={(e) => { e.stopPropagation(); handleRegenerate(s) }}>
                            Retry
                          </SydButton>
                        )}
                        <Icon name={isOpen ? 'expandLess' : 'expandMore'} customSize={16} />
                      </FlexRow>
                    </div>
                    {isOpen && s.summaryText && (
                      <div className={classes.summaryBody}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.summaryText}</ReactMarkdown>
                      </div>
                    )}
                    {isOpen && s.status === 'failed' && s.errorMessage && (
                      <div className={classes.summaryBody} style={{ color: 'var(--color-danger-500)' }}>
                        {s.errorMessage}
                      </div>
                    )}
                  </div>
                </Grid>
              )
            })}
            {showAddSummary && (
              <Grid item xs={12}>
                <div className={classes.summaryCard} style={{ padding: 14 }}>
                  <FlexColumn className='gap-sm'>
                    <SydLabel label='Summary Type'>
                      <SydSelect
                        options={addSummaryTypeOptions}
                        placeholder='Select a summary type...'
                        value={newSummaryType}
                        onChange={handleAddSummaryTypeChange}
                        disabled={addingSummary}
                      />
                      {newSummaryType === '__custom__' && (
                        <SydInput
                          width='inherit'
                          placeholder='Custom summary type'
                          value={newSummaryCustomName}
                          onChange={(e) => setNewSummaryCustomName(e?.target?.value ?? e)}
                          disabled={addingSummary}
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </SydLabel>
                    <SydLabel label='Prompt'>
                      <TextareaAutosize
                        style={{ width: '100%', fontFamily: 'inherit', fontSize: 'var(--font-size-sm)' }}
                        minRows={3}
                        value={newSummaryPrompt}
                        onChange={(e) => setNewSummaryPrompt(e.target.value)}
                        disabled={addingSummary}
                      />
                    </SydLabel>
                    <FlexRow className='gap-sm justify-end'>
                      <SydButton variant='ghost' size='sm' onClick={() => { setShowAddSummary(false); setNewSummaryType(''); setNewSummaryCustomName(''); setNewSummaryPrompt('') }} disabled={addingSummary}>
                        Cancel
                      </SydButton>
                      <SydButton
                        variant='primary'
                        size='sm'
                        onClick={handleSubmitNewSummary}
                        processing={addingSummary}
                        disabled={!(newSummaryType === '__custom__' ? newSummaryCustomName.trim() : newSummaryType) || !newSummaryPrompt}
                      >
                        Add Summary
                      </SydButton>
                    </FlexRow>
                  </FlexColumn>
                </div>
              </Grid>
            )}
          </>
        )}

        {TERMINAL_STATUSES.includes(meeting.status) && (
          <>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <button type='button' className={classes.toggleBtn} onClick={() => setShowTranscript(prev => !prev)}>
                <Icon name={showTranscript ? 'expandLess' : 'expandMore'} customSize={16} />
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </button>
              {showTranscript && transcriptData?.available && (
                <div className='font-size-sm mt-2' style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, maxHeight: 500, overflow: 'auto' }}>
                  {transcriptData.transcriptText}
                </div>
              )}
              {showTranscript && transcriptData && !transcriptData.available && (
                <div className='font-size-sm mt-2' style={{ color: 'var(--dai-color-text-dim)' }}>
                  <em>Transcript not yet available</em>
                </div>
              )}
            </Grid>
          </>
        )}

        <Grid item xs={12}><Divider /></Grid>
        <Grid item xs={12}>
          <button type='button' className={classes.toggleBtn} onClick={() => setShowEvents(prev => !prev)}>
            <Icon name={showEvents ? 'expandLess' : 'expandMore'} customSize={16} />
            {showEvents ? 'Hide Events' : 'Show Events'} ({events.length})
          </button>
          {showEvents && (
            <table className={classes.eventsTable}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Source</th>
                  <th>Event</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {events.map(evt => (
                  <tr key={evt.meetingEventId}>
                    <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{dayjs(evt.createdDate).format('h:mm:ss A')}</td>
                    <td>{evt.eventSource}</td>
                    <td className={classes.eventType}>{evt.eventType}</td>
                    <td>{evt.status && <MeetingStatusBadge status={evt.status} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Grid>
      </Grid>

      <SydModal
        title='Generate Enhanced Notes'
        open={enhancedPromptOpen}
        onClose={() => setEnhancedPromptOpen(false)}
        size='mediumLarge'
        position='top'
        disablePortal={false}
      >
        {enhancedPromptOpen && (
          <div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SydLabel label='Prompt'>
                  <TextareaAutosize
                    className={classes.modalPromptArea}
                    minRows={6}
                    maxRows={16}
                    value={enhancedPromptValue}
                    onChange={(e) => setEnhancedPromptValue(e.target.value)}
                  />
                </SydLabel>
                <div className='font-size-xs mt-1' style={{ color: 'var(--dai-color-text-dim)' }}>
                  Your notes and the meeting transcript will be provided to the model automatically.
                </div>
              </Grid>
              <Grid item xs={12}>
                <SydModalActions>
                  <SydButton variant='ghost' size='md' onClick={() => setEnhancedPromptOpen(false)}>
                    Cancel
                  </SydButton>
                  <SydButton
                    variant='primary'
                    size='md'
                    onClick={handleSubmitEnhancedNotes}
                    processing={addingSummary || regenerating}
                    disabled={!enhancedPromptValue.trim()}
                  >
                    Generate
                  </SydButton>
                </SydModalActions>
              </Grid>
            </Grid>
          </div>
        )}
      </SydModal>
    </PageLayout>
  )
}

export default MeetingDetail
